import { decodeHtml } from '../../utils/common';

/**
 * Formats the filter data into an object with hierarchy.
 *
 * @param {array} data
 *   Data in the format as it comes from Drupal.
 *
 * @returns {array}
 *   Returns formatted array for use in the map filter.
 */
const FormattedResidenceFilterData = ( data, showByFieldName, validFloorPlanUuids ) => {
  const levelOfCare = {};
  const units = {};
  const allLevelsOfCare = {};

  // Safety check: ensure data is an array
  if (!Array.isArray(data)) {
    console.warn('FormattedResidenceFilterData: expected data to be an array, received:', typeof data);
    return { levelOfCare, units, allLevelsOfCare };
  }

  data.forEach( ( rawItem ) => {
    // When the results are from Fusejs, they have an 'item' property,
    // so normalize this so that this function works with original data
    // and filtered data.

    let item = rawItem;
    if ( Object.prototype.hasOwnProperty.call( item, 'item' ) ) {
      item = item.item;
    }

    // If showByFieldName is specified, filter items based on that field
    if ( showByFieldName ) {
      if ( showByFieldName === 'field_display_on_floor_plans_pag' ) {
        // For floor plans page: only include units whose floor plan is in the valid list
        if ( validFloorPlanUuids && validFloorPlanUuids.length > 0 ) {
          const floorPlanUuid = item.floor_plan?.uuid;
          if ( !floorPlanUuid || !validFloorPlanUuids.includes(floorPlanUuid) ) {
            return;
          }
        }
      } else {
        // For other fields: check if the field exists on the item itself
        const itemHasField = showByFieldName in item;
        const itemFieldValue = item[showByFieldName];

        if (!itemHasField || !itemFieldValue) {
          return;
        }
      }
    }

    // If the item doesn't have an svg_id or field_svg_id or svgId, it's not a map unit
    // (e.g., it could be a floor plan from the floor plans page)
    // In that case, we still want to include it in the filter, but not in the units object
    const unitSvgId = item?.field_svg_id || item?.svg_id || item?.svgId;
    if ( Object.prototype.hasOwnProperty.call( item, 'field_svg_id' )
      || Object.prototype.hasOwnProperty.call( item, 'svg_id' )
      || Object.prototype.hasOwnProperty.call( item, 'svgId' ) ) {
      units[unitSvgId] = {
        uuid: item.uuid,
      };
    }

    // Add Level of Care Uuid to array. We're recording it here because we
    // want the actual filter to show all available LOCS, not just the ones
    // that match later filter panels.

    const locUuid = item.level_of_care?.uuid;
    // Something doesn't have a level of care, it can't show up in the filter.
    if ( locUuid && !Object.prototype.hasOwnProperty.call( levelOfCare, locUuid ) ) {
      const locLabel = item.level_of_care?.name;
      const locWeight = item.level_of_care?.weight;

      // Weight comes over as string, so we convert it to a number.
      const rawWeight = parseInt( locWeight, 10 );
      const weight = Number.isNaN( rawWeight ) ? 100 : rawWeight;

      levelOfCare[locUuid] = {
        label: locLabel ?? '',
        weight,
        children: {
          floor_plan_category: { label: 'Types', id: 'floor_plan_category', children: {} },
          bedroom_data: { label: 'Bedrooms', id: 'bedroom_data', children: {} },
          map_building_grouping: { label: 'Locations', id: 'map_building_grouping', children: {} },
          floor_plan: { label: 'Floor Plans', id: 'floor_plan', children: {} },
          residence_status: { label: 'Occupancy', id: 'residence_status', children: {} },
          level_of_care: { label: 'Levels of Care', id: 'level_of_care', children: {} },
          favorite_residences: { label: 'Favorite Residences', id: 'favorite_residences', children: {} },
        },
      };

      allLevelsOfCare[locUuid] = {
        label: locLabel ?? '',
        weight,
      };

      if (units[unitSvgId]) {
        units[unitSvgId].level_of_care = locUuid;
      }
    }

    if ( Object.prototype.hasOwnProperty.call( item, 'bedroom_data' ) ) {
      const bedroomsUuid = item.bedroom_data?.uuid || 'none';
      const bedroomsLabel = item.bedroom_data?.name;
      const bedroomsWeight = item.bedroom_data?.weight;

      // Weight comes over as string, so we convert it to a number.
      const rawWeight = parseInt( bedroomsWeight, 10 );
      const weight = Number.isNaN( rawWeight ) ? 100 : rawWeight;

      if(bedroomsUuid && bedroomsUuid !== 'none' && locUuid && levelOfCare[locUuid]) {
        levelOfCare[locUuid].children.bedroom_data.children[bedroomsUuid] = {
          label: bedroomsLabel,
          uuid: bedroomsUuid,
          weight,
          meta: item,
        };
      }

      if (units[unitSvgId]) {
        units[unitSvgId].bedroom_data = bedroomsUuid;
      }
    }

    // Locations data.
    if ( Object.prototype.hasOwnProperty.call( item, 'map_building_grouping' ) ) {
      const locationUuid = item.map_building_grouping?.uuid || 'none';
      const locationLabel = item.map_building_grouping?.name || 'Not Applicable';
      const locationWeight = item.map_building_grouping?.weight;

      // Weight comes over as string, so we convert it to a number.
      const rawWeight = parseInt( locationWeight, 10 );
      const weight = Number.isNaN( rawWeight ) ? 100 : rawWeight;

      if(locationUuid && locationUuid !== 'none' && locUuid && levelOfCare[locUuid]) {
        levelOfCare[locUuid].children.map_building_grouping.children[locationUuid] = {
          label: locationLabel,
          uuid: locationUuid,
          weight,
          meta: item,
        };
      }

      if (units[unitSvgId]) {
        units[unitSvgId].map_building_grouping = locationUuid;
      }
    }

    // Types data.
    if ( Object.prototype.hasOwnProperty.call( item, 'floor_plan_category' ) ) {
      const typeUuid = item.floor_plan_category?.uuid || 'none';
      const typeLabel = item.floor_plan_category?.name;
      const typeWeight = item.floor_plan_category?.weight;

      // Weight comes over as string, so we convert it to a number.
      const rawWeight = parseInt( typeWeight, 10 );
      const weight = Number.isNaN( rawWeight ) ? 100 : rawWeight;

      if (typeUuid && typeUuid !== 'none' && locUuid && levelOfCare[locUuid]) {
        levelOfCare[locUuid].children.floor_plan_category.children[typeUuid] = {
          label: typeLabel,
          uuid: typeUuid,
          weight,
          meta: item,
        };
      }

      if (units[unitSvgId]) {
        units[unitSvgId].floor_plan_category = typeUuid;
      }
    }

    // Occupancy Data.
    if ( Object.prototype.hasOwnProperty.call( item, 'residence_status' ) ) {
      const occupancyUuid = item.residence_status?.uuid || 'none';
      const occupancyLabel = item.residence_status?.name || 'Not Applicable';
      const occupancyWeight = item.residence_status?.weight;

      // Weight comes over as string, so we convert it to a number.
      const rawWeight = parseInt( occupancyWeight, 10 );
      const weight = Number.isNaN( rawWeight ) ? 100 : rawWeight;

      if(occupancyUuid && occupancyUuid !== 'none' && locUuid && levelOfCare[locUuid]) {
        levelOfCare[locUuid].children.residence_status.children[occupancyUuid] = {
          label: decodeHtml( occupancyLabel ),
          uuid: occupancyUuid,
          weight,
          meta: item,
        };
      }

      if (units[unitSvgId]) {
        units[unitSvgId].residence_status = occupancyUuid;
      }
    }

    // Floor Plan Data.
    if ( Object.prototype.hasOwnProperty.call( item, 'floor_plan' ) ) {
      const floorPlanUuid = item.floor_plan?.uuid || 'none';
      const floorPlanLabel = item.floor_plan?.title || 'Not Applicable';
      const floorPlanWeight = item.floor_plan?.weight;

      // Weight comes over as string, so we convert it to a number.
      const rawWeight = parseInt( floorPlanWeight, 10 );
      const weight = Number.isNaN( rawWeight ) ? 100 : rawWeight;

      if (floorPlanUuid && floorPlanUuid !== 'none' && locUuid && levelOfCare[locUuid]) {
        const rawKeyColor = item.key_color;
        const keyColor = ( rawKeyColor && rawKeyColor !== '' ) ? rawKeyColor : '#000000';
        levelOfCare[locUuid].children.floor_plan.children[floorPlanUuid] = {
          label: decodeHtml( floorPlanLabel ),
          uuid: floorPlanUuid,
          weight,
          keyColor,
          meta: item,
        };
      }

      if (units[unitSvgId]) {
        units[unitSvgId].floor_plan = floorPlanUuid;
      }
    }
  } );

  // Add the favorite residences filter option to each level of care
  Object.keys( levelOfCare ).forEach( ( locUuid ) => {
    levelOfCare[locUuid].children.favorite_residences.children.show_favorites = {
      label: 'Show only favorite residences',
      uuid: 'show_favorites',
      weight: 1,
    };
  } );

  return {
    levelOfCare,
    units,
    allLevelsOfCare,
  };
};

export default FormattedResidenceFilterData;
